import { tasVersion } from 'App/components/config/config';
import { resetLogoutGuard } from 'App/utils/createAxios';

let accessToken = null;
let refreshPromise = null;

const listeners = new Set();

const endpoint = `/tas/${tasVersion}`;

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('therigy_token_sync') : null;

const CHANNEL_EVENTS = Object.freeze({
    TOKEN_UPDATE: 'TOKEN_UPDATE',
    TOKEN_CLEAR: 'TOKEN_CLEAR',
});

const LOGOUT_FLAG = 'therigy_logged_out';

if (channel) {
    channel.onmessage = (event) => {
        const { type, access } = event.data;
        if (type === CHANNEL_EVENTS.TOKEN_UPDATE) {
            accessToken = access;
            resetLogoutGuard();
            sessionStorage.removeItem(LOGOUT_FLAG);
            listeners.forEach((fn) => fn({ access: accessToken }));
        } else if (type === CHANNEL_EVENTS.TOKEN_CLEAR) {
            accessToken = null;
            refreshPromise = null;
            sessionStorage.setItem(LOGOUT_FLAG, 'true');
            listeners.forEach((fn) => fn({ access: null }));
        }
    };
}

class TokenManagerService {
    static markLoggedOut() {
        sessionStorage.setItem(LOGOUT_FLAG, 'true');
    }

    static isLoggedOut() {
        return sessionStorage.getItem(LOGOUT_FLAG) === 'true';
    }

    static clearLogoutFlag() {
        sessionStorage.removeItem(LOGOUT_FLAG);
    }

    static getAccessToken() {
        return accessToken;
    }

    static setTokens({ access }) {
        accessToken = access;
        resetLogoutGuard();
        sessionStorage.removeItem(LOGOUT_FLAG);
        listeners.forEach((fn) => fn({ access }));

        // notify other tabs
        if (channel) {
            channel.postMessage({ type: CHANNEL_EVENTS.TOKEN_UPDATE, access: accessToken });
        }
    }

    // Clears this tab's own token state only. Does NOT broadcast to other tabs —
    // a single tab's refresh failure isn't proof every tab sharing the session is dead.
    static clearTokensLocal() {
        accessToken = null;
        refreshPromise = null;
        listeners.forEach((fn) => fn({ access: null }));
    }

    // Full logout: clears this tab's state AND tells every other open tab to log out too.
    // Reserve this for explicit, confirmed logout (e.g. AuthService.clearSession/logout).
    static clearTokens() {
        TokenManagerService.clearTokensLocal();

        // Notify other tabs
        if (channel) {
            channel.postMessage({ type: CHANNEL_EVENTS.TOKEN_CLEAR });
        }
    }

    /**
     * Subscribe to token changes (for React Context, AngularJS digest, etc.)
     * @param {Function} listener
     * @returns {Function} unsubscribe
     */
    static subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    }

    static async fetchInitialRefresh(token) {
        const { default: Axios } = await import('axios');
        await Axios.get(`${endpoint}/refresh`, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return TokenManagerService.refreshAccessToken();
    }

    /**
     * Ensures only one refresh request is in-flight at a time.
     * @returns {Promise<string>} new access token
     */
    static async refreshAccessToken(force = false) {
        if (refreshPromise) {
            return refreshPromise;
        }

        const tokenBeforeLock = accessToken;

        const runRefresh = async () => {
            try {
                // Another tab refreshed while this tab waited for the lock.
                if (!force && accessToken && accessToken !== tokenBeforeLock) {
                    return accessToken;
                }

                const { default: Axios } = await import('axios');
                const response = await Axios.post(`${endpoint}/refresh`, null, {
                    withCredentials: true,
                });
                const { accessToken: newAccess } = response.data;
                TokenManagerService.setTokens({ access: newAccess });
                return newAccess;
            } catch (error) {
                if (error?.response?.status === 401) {
                    // This tab's refresh token is dead — clear locally. Do not broadcast:
                    // a bare 401 from this one request isn't confirmation every other tab
                    // sharing the session is also dead.
                    TokenManagerService.clearTokensLocal();
                }
                throw error;
            } finally {
                refreshPromise = null;
            }
        };

        refreshPromise = navigator.locks?.request
            ? navigator.locks.request('therigy_token_refresh', runRefresh)
            : runRefresh();
        return refreshPromise;
    }

    static clearSession() {
        TokenManagerService.clearTokens();
    }
}

export default TokenManagerService;
